import { useQuery } from '@tanstack/react-query';
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { supabase } from '@/lib/supabase';
import { getAuthToken, getCurrentUserId } from '@/lib/auth-token';
import { useAuth } from '@/lib/auth';
import { unleashEnabled } from '@/lib/feature-flags';
import { useUserRole } from '@/hooks/useUserRole';
import type { Course } from '@/types/courses';

interface UseCoursesOptions {
  courseType?: 'standard' | 'nextgen';
}

export const courseKeys = {
  all: ['courses'] as const,
  byType: (type: string) => [...courseKeys.all, type] as const,
  byId: (id: string) => [...courseKeys.all, 'detail', id] as const,
};

// ---------------------------------------------------------------------------
// Lesson source: base `lessons` vs the `lessons_catalog` view
// (course-video-id-via-signer, A4 7WS3IGQniWzXHUvFSpsb, parent CDJ8i3r6qGzvukL5OMhJ)
//
// Base `lessons` hands any signed-in user `video_id`, and the id alone was
// enough to fetch a paid video (download.mp4). With the flag ON, non-staff read
// the `lessons_catalog` view instead: no `video_id`, plus `has_video`. The
// player then gets the id from the access-checked signer (/pay/video/sign with
// {lessonId}). Staff (admin, content_manager) stay on base `lessons`, because
// they need drafts and the raw id for editing. Partners take the catalog path.
//
// Flag OFF: source is always 'base', and the select, query key and rows are
// exactly what they were before this flag existed.
// ---------------------------------------------------------------------------

export const LESSON_SOURCE_FLAG = 'course-video-id-via-signer';

export type LessonSource = 'base' | 'catalog';

export interface LessonSourceInputs {
  /** Build has an Unleash provider (src/lib/feature-flags.ts unleashEnabled). */
  unleashEnabled: boolean;
  flagsReady: boolean;
  flagsError: boolean;
  flagOn: boolean;
  authLoading: boolean;
  /**
   * The request will carry a JWT: firebaseAuth.currentUser is set (see
   * useLessonSource). False = the read runs as anon.
   */
  hasUser: boolean;
  roleLoading: boolean;
  isStaff: boolean;
}

export interface LessonSourceDecision {
  /** False while the answer is not yet known. Callers must not query yet. */
  ready: boolean;
  source: LessonSource;
  /** The flag's effective value (false until Unleash has resolved). */
  flagOn: boolean;
}

/**
 * Pure decision behind useLessonSource. Exported for tests.
 *
 * Waits for Unleash before answering (review M4): useFlag() reads false during
 * the SDK bootstrap window, so deciding early would fire a base-table read and
 * then a catalog read. A build without a provider, or an Unleash error, counts
 * as resolved (flag OFF), so the courses never hang on a flag service outage.
 *
 * With the flag ON it also waits for auth and the role. Otherwise a staff user
 * whose role is still loading would be routed to the catalog (no drafts, no
 * video_id) for one fetch.
 *
 * A logged-out visitor stays on base (Vgz8IPjSLq4B05qaCzun). anon has no SELECT
 * on lessons_catalog by design, so a catalog embed fails the whole courses
 * request with 401/42501; the base read returns [] for anon (every
 * courses/modules/lessons policy is TO authenticated), which is exactly what
 * flag OFF gives. Do NOT fix this by granting anon SELECT on the view.
 * The no-user check runs before the role wait: useUserRole is disabled with no
 * user, and must not be what decides.
 */
export function resolveLessonSource(i: LessonSourceInputs): LessonSourceDecision {
  const flagsResolved = !i.unleashEnabled || i.flagsReady || i.flagsError;
  if (!flagsResolved) return { ready: false, source: 'base', flagOn: false };
  if (!i.flagOn) return { ready: true, source: 'base', flagOn: false };
  if (i.authLoading) return { ready: false, source: 'base', flagOn: true };
  if (!i.hasUser) return { ready: true, source: 'base', flagOn: true };
  if (i.roleLoading) return { ready: false, source: 'base', flagOn: true };
  return { ready: true, source: i.isStaff ? 'base' : 'catalog', flagOn: true };
}

export function useLessonSource(): LessonSourceDecision {
  // Opt out of the React Compiler here (code-reviewer M1, ledger
  // cSkQZ0BdXQsey2QsemLo). getCurrentUserId() reads firebaseAuth.currentUser,
  // which is outside React. Compiled, the hook memoized that read on its React
  // inputs, so a consumer that was already mounted when signIn() ran kept the
  // logged-out answer ('base') after sign-in: a JWT-bearing read of base
  // lessons, video_id included. Uncompiled, every render (and signIn() causes
  // several, through `loading`) re-reads it. Pinned by the "M1 probe" test in
  // src/hooks/__tests__/useCourses.source.test.tsx, which fails without this.
  'use no memo';
  const flagOn = useFlag(LESSON_SOURCE_FLAG);
  const { flagsReady, flagsError } = useFlagsStatus();
  const { loading: authLoading } = useAuth();
  const { data: role, isLoading: roleLoading } = useUserRole();
  return resolveLessonSource({
    unleashEnabled,
    flagsReady,
    flagsError: !!flagsError,
    flagOn,
    authLoading,
    // "Signed in" means "this request will carry a JWT". customFetch
    // (src/lib/supabase.ts) takes the token from firebaseAuth.currentUser at
    // fetch time, so that is the one signal read here, deliberately NOT React's
    // `user` as well:
    // - sign-in: signIn() clears `loading` (its finally) before the
    //   onIdTokenChanged listener (src/lib/auth.tsx) sets `user`, so React
    //   alone would call a signed-in caller anon and send them to base lessons
    //   (video_id).
    // - sign-out: React's `user` outlives currentUser for a moment, and
    //   counting it would send an anon request to lessons_catalog (401/42501).
    // This is only as fresh as the last render; nothing re-renders on a
    // currentUser change by itself. A narrow window remains between render and
    // fetch (the 5 s auth failsafe restoring a session in between); see L1 in
    // cSkQZ0BdXQsey2QsemLo.
    hasUser: getCurrentUserId() !== null,
    roleLoading,
    isStaff: role?.isStaff === true,
  });
}

/**
 * The PostgREST embed for a lesson list. Aliased back to `lessons` on the
 * catalog path, so every reader of `module.lessons` keeps working.
 * `columns` must not include video_id on the catalog path (the view has none).
 */
export function lessonsEmbed(source: LessonSource, columns: string): string {
  return source === 'catalog' ? `lessons:lessons_catalog(${columns})` : `lessons(${columns})`;
}

/**
 * Query key for a course query. Flag OFF returns `base` unchanged (the key the
 * app has always used). Flag ON appends the source, so staff rows and catalog
 * rows never share a cache entry, and a flag flip never mixes the two shapes.
 * Prefix invalidation (courseKeys.all, courseKeys.byId(id)) still matches both.
 */
export function courseQueryKey<K extends readonly unknown[]>(
  base: K,
  decision: Pick<LessonSourceDecision, 'flagOn' | 'source'>,
): readonly unknown[] {
  return decision.flagOn ? [...base, { lessonSource: decision.source }] : base;
}

function parseAttachments(raw: unknown) {
  if (!raw) return undefined;
  if (typeof raw === 'string') {
    try {
      return raw.trim() ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  }
  return raw;
}

function mapCourseData(data: any[], source: LessonSource = 'base'): Course[] {
  return data.map((course) => ({
    ...course,
    modules: (course.modules || [])
      .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
      .map((m: any) => ({
        ...m,
        lessons: (m.lessons || [])
          .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
          .map((l: any) =>
            source === 'catalog'
              ? {
                  ...l,
                  // The view has no video_id. null (not undefined) keeps the
                  // CourseLesson shape; readers test has_video instead.
                  video_id: null,
                  module_id: m.id,
                  attachments: parseAttachments(l.attachments),
                }
              : {
                  ...l,
                  module_id: m.id,
                  attachments: parseAttachments(l.attachments),
                },
          ),
      })),
  }));
}

export { mapCourseData as _mapCourseDataForTests };

// Base path. Byte-identical to the pre-flag select; do not edit it for the
// catalog path, edit COURSE_SELECT_CATALOG.
export const COURSE_SELECT = `
  id, title, overview, tile_description,
  course_type, is_featured, status,
  skill_domains, lesson_type,
  modules (
    id, title, description, position,
    lessons (
      id, title, content, video_id, position, status, attachments
    )
  )
`;

// Catalog path: same columns, but video_id is replaced by has_video and the
// embed is aliased back to `lessons`, so mapCourseData and m.lessons still work.
// content_locked (migrations/20260928_lessons_catalog_content_lock.sql) is true
// when the caller lacks the course's access; the view then returns content and
// attachments as NULL, and the player renders the locked state instead of an
// empty body (K794igRuJQX0VQ77pDvb).
export const COURSE_SELECT_CATALOG = `
  id, title, overview, tile_description,
  course_type, is_featured, status,
  skill_domains, lesson_type,
  modules (
    id, title, description, position,
    ${lessonsEmbed('catalog', 'id, title, content, has_video, position, status, attachments, content_locked')}
  )
`;

export function courseSelect(source: LessonSource): string {
  return source === 'catalog' ? COURSE_SELECT_CATALOG : COURSE_SELECT;
}

export function useCourses(options: UseCoursesOptions = {}) {
  const { courseType } = options;
  const lessonSource = useLessonSource();
  const { source } = lessonSource;

  const query = useQuery({
    queryKey: courseQueryKey(
      courseType ? courseKeys.byType(courseType) : courseKeys.all,
      lessonSource,
    ),
    queryFn: async (): Promise<Course[]> => {
      // Diagnostic: check if auth token is available
      const token = await getAuthToken();
      console.log('[useCourses] Auth token available:', !!token, 'length:', token?.length);

      let query = supabase
        .from('courses')
        .select(courseSelect(source))
        .order('title');

      if (courseType) {
        query = query.eq('course_type', courseType);
      }

      const { data, error } = await query;
      if (error) {
        console.error('[useCourses] PostgREST error:', error);
        throw error;
      }
      console.log('[useCourses] Got', data?.length, 'courses, courseType:', courseType);
      return mapCourseData(data || [], source);
    },
    enabled: lessonSource.ready,
    staleTime: 1000 * 60 * 10,
  });
  // A disabled query reports isLoading=false with no data, which consumers
  // would render as "no courses". Report loading until the source is known.
  return lessonSource.ready ? query : { ...query, isLoading: true };
}

export function useCourse(courseId: string | undefined) {
  const lessonSource = useLessonSource();
  const { source } = lessonSource;

  const query = useQuery({
    queryKey: courseQueryKey(courseKeys.byId(courseId || ''), lessonSource),
    queryFn: async (): Promise<Course | null> => {
      if (!courseId) return null;
      const { data, error } = await supabase
        .from('courses')
        .select(courseSelect(source))
        .eq('id', courseId)
        .single();

      if (error) throw error;
      if (!data) return null;
      return mapCourseData([data], source)[0];
    },
    enabled: !!courseId && lessonSource.ready,
    staleTime: 1000 * 60 * 10,
  });
  // Same as useCourses: without this, CoursePlayer would render
  // "course not found" during the flag bootstrap window.
  return !!courseId && !lessonSource.ready ? { ...query, isLoading: true } : query;
}
